/**
 * Security configuration and utilities
 */

// Content Security Policy configuration
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for Vite in development
    "'unsafe-eval'", // Required for Vite in development
    'https://www.googletagmanager.com',
    'https://www.google-analytics.com',
    'https://connect.facebook.net'
  ],
  'style-src': [
    "'self'",
    "'unsafe-inline'" // Required for dynamic styles
  ],
  'img-src': [
    "'self'",
    'data:',
    'https:',
    'blob:'
  ],
  'connect-src': [
    "'self'",
    'https://api.cassette.diy',
    'https://www.google-analytics.com',
    'https://api.logtail.com',
    'https://www.googletagmanager.com',
    'https://connect.facebook.net'
  ],
  'font-src': [
    "'self'",
    'data:'
  ],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"]
};

// Security headers configuration
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=(), interest-cohort=()'
};

// Rate limiting configuration
export const RATE_LIMITS = {
  API_CALLS: {
    maxRequests: 100,
    windowMs: 15 * 60 * 1000, // 15 minutes
  },
  SEARCH: {
    maxRequests: 20,
    windowMs: 60 * 1000, // 1 minute
  },
  UPLOAD: {
    maxRequests: 10,
    windowMs: 60 * 1000, // 1 minute
  }
};

// Input validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  USER_ID: /^[a-zA-Z0-9_-]{1,50}$/,
  TRACK_NAME: /^[a-zA-Z0-9\s\-_.,!?()]{1,100}$/,
  SEARCH_QUERY: /^[a-zA-Z0-9\s\-_.,!?()@]{1,100}$/,
  URL: /^https?:\/\/[^\s/$.?#].[^\s]*$/i
};

// File upload security
export const FILE_UPLOAD_SECURITY = {
  MAX_FILE_SIZE: 2 * 1024 * 1024 * 1024, // 2GB
  ALLOWED_AUDIO_TYPES: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/aac', 'audio/flac'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/mov', 'video/avi', 'video/mkv'],
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  DANGEROUS_EXTENSIONS: ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar']
};

// Session security
export const SESSION_SECURITY = {
  TIMEOUT_MS: 24 * 60 * 60 * 1000, // 24 hours
  REFRESH_THRESHOLD_MS: 5 * 60 * 1000, // 5 minutes before expiry
  MAX_CONCURRENT_SESSIONS: 3
};

// Logging security
export const LOGGING_SECURITY = {
  SENSITIVE_FIELDS: ['password', 'token', 'secret', 'key', 'auth'],
  MAX_LOG_LENGTH: 1000,
  SANITIZE_LOGS: true
};

/**
 * Sanitize log data to prevent sensitive information leakage
 */
export const sanitizeLogData = (data: any): any => {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  const sanitized = { ...data };
  
  for (const key in sanitized) {
    if (LOGGING_SECURITY.SENSITIVE_FIELDS.some(field => 
      key.toLowerCase().includes(field.toLowerCase())
    )) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeLogData(sanitized[key]);
    } else if (typeof sanitized[key] === 'string' && 
               sanitized[key].length > LOGGING_SECURITY.MAX_LOG_LENGTH) {
      sanitized[key] = sanitized[key].substring(0, LOGGING_SECURITY.MAX_LOG_LENGTH) + '...';
    }
  }
  
  return sanitized;
};

/**
 * Validate file type for upload
 */
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

/**
 * Validate file size
 */
export const validateFileSize = (file: File, maxSize: number): boolean => {
  return file.size <= maxSize;
};

/**
 * Check if file extension is dangerous
 */
export const isDangerousFile = (filename: string): boolean => {
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return FILE_UPLOAD_SECURITY.DANGEROUS_EXTENSIONS.includes(extension);
};